import React from 'react';

import { Bool } from './components/Bool';
import { EventList } from './components/EventList';
import { Expiries } from './components/Expiries';
import { ExpiriesLimit } from './components/ExpiriesLimit';
import { ExpiriesPercentageValueList } from './components/ExpiriesPercentageValue';
import { ExpiriesValueList } from './components/ExpiriesValue';
import { HardLimitHandler } from './components/HardLimitHandler';
import { InstrumentArray } from './components/InstrumentArray';
import { InstrumentsPercentageValueList } from './components/InstrumentPercentageValue';
import { InstrumentsValueList } from './components/InstrumentValue';
import { Num } from './components/Num';
import { NumRange } from './components/NumRange';
import { OneWayHandlerList } from './components/OneWayHandler';
import { TenorBucketHandlerList } from './components/TenorBucketHandlerList';
import { TenorBucketPercentageHandlerList } from './components/TenorBucketPercentageHandlerList';
import { Text } from './components/Text';
import { TextArray } from './components/TextArray';
import { TextValueList } from './components/TextValueArray';
import { BaseInputProps, ParamType } from './types';
import {
  emptyValidator,
  expiriesValidator,
  isEmpty,
  isNullLike,
  numRangeValidator,
} from './utils';
import { ExpiryVegaAlphaByEdgeList, validateVegaRules } from './components/ExpiryVegaAlphaByEdgeList';
import { SweeperList, validateSweeperRules } from './components/SweeperList';

function emptyErrText(index: number, key?: string) {
  return `The ${index + 1}${
    index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'
  } item: ${key ? key + ' ' : ''}should not be empty`;
}

function zeroErrText(index: number, key?: string, type?: 'min' | 'max', canZero?: boolean) {
  return `The ${index + 1}${
    index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'
  } item: ${key ? key + ' ' : ''}should be ${
    type === 'min' ? (canZero ? '<=' : '<') : canZero ? '>=' : '>'
  } 0`;
}

export const ParamTypeConfig: Record<
  ParamType,
  {
    name: string;
    key?: string; // 在服务端 value 的结构体中实际存放控件值的字段，比如 { number: '1' } 中的 'number'
    portalKey?: string;
    defaultValue?: any;
    Comp: React.ComponentType<BaseInputProps>;
    validator?(value: any, enableEmpty?: boolean): string;
    required?: boolean;
    sort: number;
    modalWidth?: number;
    labelSpan?: number;
  }
> = {
  [ParamType.ParamGroup]: {
    name: '参数组',
    Comp: () => <></>,
    sort: 12,
  },
  [ParamType.Number]: {
    name: '普通数值',
    key: 'number',
    portalKey: 'numericalValue',
    Comp: Num,
    validator: emptyValidator,
    required: true,
    sort: 1,
  },
  [ParamType.Percentage]: {
    name: '百分比数值',
    key: 'number',
    portalKey: 'percentageValue',
    Comp: props => <Num {...props} isPercentage />,
    validator: emptyValidator,
    required: true,
    sort: 2,
    modalWidth: 700,
  },
  [ParamType.Bool]: {
    name: 'TRUE/FALSE',
    key: 'bool',
    portalKey: 'booleanValue',
    Comp: Bool,
    validator: emptyValidator,
    required: true,
    sort: 3,
  },
  [ParamType.NumberRange]: {
    name: '数值范围',
    key: 'range',
    portalKey: 'numericalRangeValue',
    Comp: NumRange,
    validator: numRangeValidator,
    required: true,
    sort: 4,
  },
  [ParamType.PercentageRange]: {
    name: '百分比范围',
    key: 'range',
    portalKey: 'percentageRangeValue',
    Comp: props => <NumRange {...props} isPercentage />,
    validator: numRangeValidator,
    required: true,
    sort: 5,
  },
  [ParamType.Text]: {
    name: '文本',
    key: 'text',
    portalKey: 'textValue',
    Comp: Text,
    validator: emptyValidator,
    sort: 6,
  },
  [ParamType.Phone]: {
    name: '文本列表',
    key: 'list',
    portalKey: 'textListValue',
    Comp: TextArray,
    validator: $val => {
      if (!$val?.length) return '';
      const index = $val.findIndex(($it: string) => !$it.trim());
      if (index > -1) return emptyErrText(index);
      return '';
    },
    sort: 7,
    defaultValue: [],
  },
  [ParamType.Expiry]: {
    name: '到期日',
    key: 'list',
    portalKey: 'expiryListValue',
    Comp: Expiries,
    validator: expiriesValidator,
    sort: 8,
    defaultValue: [],
  },
  [ParamType.ExpiryValue]: {
    name: '到期日 Value',
    key: 'expiryNumberList',
    portalKey: 'expiryNumberListValue',
    Comp: ExpiriesValueList,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        const error = emptyValidator(val?.expiry);
        if (isEmpty(val.expiry) && isNullLike(val.value)) return '';
        if (error) {
          return `Expiry field of the ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: ${error}`;
        }
        if (isNullLike(val.expiry) && !isNullLike(val.value)) {
          return emptyErrText(i, 'expiry');
        }
        if (!isNullLike(val.expiry) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 9,
    modalWidth: 750,
    defaultValue: [],
  },
  [ParamType.ExpiryLimit]: {
    name: '到期日 Limit',
    key: 'expiryLimitList',
    portalKey: 'expiryLimitListValue',
    Comp: ExpiriesLimit,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        const error = emptyValidator(val?.expiry);
        if (error) {
          // return { [i]: { expire: error } }
          return `Expiry field of the ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: ${error}`;
        }
        if (
          (isNullLike(val?.minAumPercentage) ||
            isNullLike(val?.maxAumPercentage)) &&
          (isNullLike(val?.minAbs) || isNullLike(val?.maxAbs))
        ) {
          // return {
          //   [i]: 'at least one parameter must be specified between AUM and ABS',
          // }
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: at least one parameter must be specified between AUM and ABS`;
        }
        if (
          (!isNullLike(val?.minAbs) || !isNullLike(val?.maxAbs)) &&
          +val.minAbs >= +val.maxAbs
        )
          // return { [i]: { minAbs: 'Min ABS should smaller than Max ABS' } }
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: Min ABS should smaller than Max ABS`;
        if (
          (!isNullLike(val?.minAumPercentage) ||
            !isNullLike(val?.maxAumPercentage)) &&
          +val.minAumPercentage >= +val.maxAumPercentage
        ) {
          // return {
          //   [i]: { minAumPercentage: 'Min AUM should smaller than Max AUM' },
          // }
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: Min AUM should smaller than Max AUM`;
        }
      }
      return '';
    },
    sort: 10,
    modalWidth: 1000,
    labelSpan: 3,
    defaultValue: []
  },
  [ParamType.InstrumentValue]: {
    name: 'Instrument Value',
    key: 'instrumentNumberList',
    portalKey: 'instrumentNumberListValue',
    Comp: InstrumentsValueList,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.instrument) && isNullLike(val.value)) return '';
        if (isNullLike(val.instrument) && !isNullLike(val.value)) {
          return emptyErrText(i, 'instrument');
        }
        if (!isNullLike(val.instrument) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 11,
    modalWidth: 700,
    defaultValue: []
  },
  [ParamType.TextNumberList]: {
    name: '文本列表+普通数值',
    key: 'textNumberList',
    portalKey: 'textNumberListValue',
    Comp: TextValueList,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.key) && isNullLike(val.value)) return '';
        if (isNullLike(val.key) && !isNullLike(val.value)) {
          return emptyErrText(i, 'key');
        }
        if (!isNullLike(val.key) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 12,
    modalWidth: 700,
    defaultValue: [],
  },
  [ParamType.TextPercentageList]: {
    name: '文本列表+百分比数值',
    key: 'textPercentageList',
    portalKey: 'textPercentageListValue',
    Comp: props => <TextValueList {...props} isPercentage />,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.key) && isNullLike(val.value)) return '';
        if (isNullLike(val.key) && !isNullLike(val.value)) {
          return emptyErrText(i, 'key');
        }
        if (!isNullLike(val.key) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 13,
    defaultValue: [],
  },
  [ParamType.HardLimitHandler]: {
    name: 'HardLimitHandler',
    key: 'hardLimitHandler',
    portalKey: 'hardLimitHandlerValue',
    Comp: HardLimitHandler,
    validator: $val => {
      if (isNullLike($val.name)) {
        return `The Name should not be empty`;
      }
      if (isNullLike($val.protectThreshold)) {
        return `The ProtectThreshold should not be empty`;
      }
      if (isNullLike($val.wideningScaler)) {
        return `The WideningScaler should not be empty`;
      }
      return '';
    },
    sort: 14,
    modalWidth: 700,
    defaultValue: []
  },
  [ParamType.InstrumentArray]: {
    name: 'Instrument List',
    key: 'instrumentList',
    portalKey: 'instrumentListValue',
    Comp: InstrumentArray,
    validator: $val => {
      return '';
    },
    sort: 15,
    defaultValue: [],
  },
  [ParamType.OneWayHandler]: {
    name: 'oneWayHandler',
    key: 'oneWayHandlerList',
    portalKey: 'oneWayHandlerListValue',
    Comp: OneWayHandlerList,
    validator: $val => {
      if (!$val?.length) return '';
      const expiries: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isNullLike(val.expiry)) {
          return emptyErrText(i, 'expiry');
        }
        if (isNullLike(val.oneWayMode)) {
          return emptyErrText(i, 'OneWayMode');
        }
        if (isNullLike(val.optionType)) {
          return emptyErrText(i, 'OptionType');
        }
        if (isNullLike(val.offloadRisk)) {
          return emptyErrText(i, 'OffloadRisk');
        }
        // if (expiries.includes(val.expiry)) {
        //   return `The ${i + 1} ${
        //     i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
        //   } expiry is same as the ${expiries.indexOf(val.expiry) + 1} ${
        //     i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
        //   }`;
        // }
        expiries.push(val.expiry);
      }
      return '';
    },
    sort: 16,
    modalWidth: 900,
    labelSpan: 3,
    defaultValue: []
  },
  [ParamType.TenorBucketHandlerList]: {
    name: 'TenorBucketHandlerList',
    key: 'tenorBucketHandlerList',
    portalKey: 'tenorBucketHandlerListValue',
    Comp: TenorBucketHandlerList,
    validator: $val => {
      if (!$val?.length) return '';
      const keys: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.key) && isNullLike(val.value)) return '';
        if (isNullLike(val.key) && !isNullLike(val.value)) {
          return emptyErrText(i, 'key');
        }
        if (!isNullLike(val.key) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
        if (keys.includes(val.key)) {
          return `The ${i + 1} ${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } key is same as the ${keys.indexOf(val.key) + 1} ${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          }`;
        }
        keys.push(val.key);
      }
      return '';
    },
    sort: 17,
    modalWidth: 700,
    labelSpan: 4,
    defaultValue: []
  },
  [ParamType.TenorBucketPercentageHandlerList]: {
    name: 'TenorBucketPercentageHandlerList',
    key: 'tenorBucketPercentageHandlerList',
    portalKey: 'tenorBucketPercentageHandlerListValue',
    Comp: TenorBucketPercentageHandlerList,
    validator: $val => {
      if (!$val?.length) return '';
      const keys: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.key) && isNullLike(val.value)) return '';
        if (isNullLike(val.key) && !isNullLike(val.value)) {
          return emptyErrText(i, 'key');
        }
        if (!isNullLike(val.key) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
        if (keys.includes(val.key)) {
          return `The ${i + 1} ${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } key is same as the ${keys.indexOf(val.key) + 1} ${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          }`;
        }
        keys.push(val.key);
      }
      return '';
    },
    sort: 18,
    modalWidth: 700,
    labelSpan: 4,
    defaultValue: []
  },
  [ParamType.InstrumentPercentageList]: {
    name: 'Instrument Percentage Value',
    key: 'instrumentPercentageList',
    portalKey: 'instrumentPercentageListValue',
    Comp: InstrumentsPercentageValueList,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.instrument) && isNullLike(val.value)) return '';
        if (isNullLike(val.instrument) && !isNullLike(val.value)) {
          return emptyErrText(i, 'instrument');
        }
        if (!isNullLike(val.instrument) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 19,
    modalWidth: 700,
    defaultValue: [],
  },
  [ParamType.ExpiryPercentageList]: {
    name: '到期日 Percentage Value',
    key: 'expiryPercentageList',
    portalKey: 'expiryPercentageListValue',
    Comp: ExpiriesPercentageValueList,
    validator: $val => {
      if (!$val?.length) return '';
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        const error = emptyValidator(val?.expiry);
        if (isEmpty(val.expiry) && isNullLike(val.value)) return '';
        if (error) {
          return `Expiry field of the ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: ${error}`;
        }
        if (isNullLike(val.expiry) && !isNullLike(val.value)) {
          return emptyErrText(i, 'expiry');
        }
        if (!isNullLike(val.expiry) && isNullLike(val.value)) {
          return emptyErrText(i, 'value');
        }
      }
      return '';
    },
    sort: 20,
    modalWidth: 740,
    defaultValue: []
  },
  [ParamType.EventList]: {
    name: 'Event List',
    key: 'eventList',
    portalKey: 'eventListValue',
    Comp: EventList,
    validator: $val => {
      if (!$val?.length) return '';
      const eventNames: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (!val.eventName.trim()) {
          return emptyErrText(i, 'Event');
        }
        if (isNullLike(val.eventType)) {
          return emptyErrText(i, 'Event Type');
        }
        if (!val.timeZone.trim()) {
          return emptyErrText(i, 'Time zone');
        }
        if (!val.startTime) {
          return emptyErrText(i, 'Start time');
        }
        if (!val.durationHour) {
          return emptyErrText(i, 'DurationHour');
        }
        if (!val.extraDay) {
          return emptyErrText(i, 'ExtraDay');
        }
        if (!val.toCalibrate) {
          return emptyErrText(i, 'ToCalibrate');
        }
        eventNames.push(val.eventName);
      }
      return '';
    },
    sort: 21,
    modalWidth: 1400,
    labelSpan: 2,
    defaultValue: []
  },
  [ParamType.ExpiryVegaAlphaByEdgeList]: {
    name: 'ExpiryVegaAlphaByEdgeList',
    key: 'expiryVegaAlphaByEdgeList',
    portalKey: 'expiryVegaAlphaByEdgeListValue',
    Comp: ExpiryVegaAlphaByEdgeList,
    validator: ($val) => {
      if (!$val?.length) return '';
      const expiries: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.expiry)) {
          return emptyErrText(i, 'expiry');
        }
        if (isEmpty(val.vegaTarget)) {
          return emptyErrText(i, 'vegaTarget');
        }

        if (Number(val.vegaTarget) === 0) {
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: vega target can not be 0`;
        }

        const $$val = val.vegaRules || [];

        for (let k = 0; k < $$val.length; k++) {
          const vegaRule = $$val[k];
          if (isEmpty(vegaRule.optionType)) {
            return emptyErrText(i, 'optionType');
          }
          if (isEmpty(vegaRule.deltaRange.min)) {
            return emptyErrText(i, "delta range's min");
          }
          if (Number(vegaRule.deltaRange.min) < 0 || Number(vegaRule.deltaRange.min) > 1) {
            // return zeroErrText(i, "delta range's min", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's min only support 0 ~ 1`;
          }
          if (isEmpty(vegaRule.deltaRange.max)) {
            return emptyErrText(i, "delta range's max");
          }
          if (Number(vegaRule.deltaRange.max) < 0 || Number(vegaRule.deltaRange.max) > 1) {
            // return zeroErrText(i, "delta range's max", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's max only support 0 ~ 1`;
          }
          if (Number(vegaRule.deltaRange.max) <= Number(vegaRule.deltaRange.min)) {
            // return zeroErrText(i, "delta range's max", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's max should be > min`;
          }
          if (isEmpty(vegaRule.bidEdgeSpan.from)) {
            return emptyErrText(i, "bid edge range's from");
          }
          if (isEmpty(vegaRule.bidEdgeSpan.to)) {
            return emptyErrText(i, "bid edge range's to");
          }
          if (isEmpty(vegaRule.askEdgeSpan.from)) {
            return emptyErrText(i, "ask edge range's from");
          }
          if (isEmpty(vegaRule.askEdgeSpan.to)) {
            return emptyErrText(i, "ask edge range's to");
          }
          if (Number(val.vegaTarget) > 0) {
            if (Number(vegaRule.bidEdgeSpan.from) < 0) {
              return zeroErrText(i, "bid edge range's from", 'max', true);
            }
            if (Number(vegaRule.bidEdgeSpan.to) < 0) {
              return zeroErrText(i, "bid edge range's to", 'max', true);
            }
            if (Number(vegaRule.askEdgeSpan.from) < 0) {
              return zeroErrText(i, "ask edge range's from", 'max', true);
            }
            if (Number(vegaRule.askEdgeSpan.to) < 0) {
              return zeroErrText(i, "ask edge range's to", 'max', true);
            }
          }

          if (Number(val.vegaTarget) < 0) {
            if (Number(vegaRule.bidEdgeSpan.from) > 0) {
              return zeroErrText(i, "bid edge range's from", 'min', true);
            }
            if (Number(vegaRule.bidEdgeSpan.to) > 0) {
              return zeroErrText(i, "bid edge range's to", 'min', true);
            }
            if (Number(vegaRule.askEdgeSpan.from) > 0) {
              return zeroErrText(i, "ask edge range's from", 'min', true);
            }
            if (Number(vegaRule.askEdgeSpan.to) > 0) {
              return zeroErrText(i, "ask edge range's to", 'min', true);
            }
          }
        }
        // if (expiries.includes(val.expiry)) {
        //   return `The ${i + 1} ${
        //     i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
        //   } expiry is same as the ${expiries.indexOf(val.expiry) + 1} ${
        //     i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
        //   }`;
        // }
        const hasOverRange = validateVegaRules($$val);
        if (!hasOverRange)
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: delta range overlap`;

        expiries.push(val.expiry);
      }

      return '';
    },
    sort: 18,
    modalWidth: 1380,
    labelSpan: 2,
  },
  [ParamType.SweeperList]: {
    name: 'SweeperList',
    key: 'sweeperList',
    portalKey: 'sweeperListValue',
    Comp: SweeperList,
    validator: ($val) => {
      if (!$val?.length) return '';
      const expiries: string[] = [];
      for (let i = 0; i < $val.length; i++) {
        const val = $val[i];
        if (isEmpty(val.expiry)) {
          return emptyErrText(i, 'expiry');
        }
        if (isEmpty(val.vegaTarget)) {
          return emptyErrText(i, 'vegaTarget');
        }

        if (Number(val.vegaTarget) === 0) {
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: vega target can not be 0`;
        }

        const $$val = val.sweeperRules || [];

        for (let k = 0; k < $$val.length; k++) {
          const sweeperRules = $$val[k];
          if (isEmpty(sweeperRules.optionType)) {
            return emptyErrText(i, 'optionType');
          }
          if (isEmpty(sweeperRules.deltaRange.min)) {
            return emptyErrText(i, "delta range's min");
          }
          if (Number(sweeperRules.deltaRange.min) < 0 || Number(sweeperRules.deltaRange.min) > 1) {
            // return zeroErrText(i, "delta range's min", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's min only support 0 ~ 1`;
          }
          if (isEmpty(sweeperRules.deltaRange.max)) {
            return emptyErrText(i, "delta range's max");
          }
          if (Number(sweeperRules.deltaRange.max) < 0 || Number(sweeperRules.deltaRange.max) > 1) {
            // return zeroErrText(i, "delta range's max", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's max only support 0 ~ 1`;
          }
          if (Number(sweeperRules.deltaRange.max) <= Number(sweeperRules.deltaRange.min)) {
            // return zeroErrText(i, "delta range's max", 'max', true);
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: delta range's max should be > min`;
          }
          if (!(Number(sweeperRules.sweeperBufferInVols) || Number(sweeperRules.sweeperBufferInBps))) {
            return `The ${i + 1}${
              i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
            } item: Sweeper Buffer can not be entirely empty or 0`;
          }
        }
        const hasOverRange = validateSweeperRules($$val);
        if (!hasOverRange)
          return `The ${i + 1}${
            i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
          } item: delta range overlap`;

        expiries.push(val.expiry);
      }

      return '';
    },
    sort: 19,
    modalWidth: 1380,
    labelSpan: 2,
  },
};

export function paramValidator(
  val: any,
  values: { valueType: ParamType },
  enableEmpty?: boolean,
) {
  const config = ParamTypeConfig[values.valueType];
  if (!config) return '';
  const value = config.key ? val?.[config.key] : val;
  return config.validator?.(value, enableEmpty) || '';
}

export const ParamTypeOptions = Object.entries(ParamTypeConfig)
  .map(([value, config]) => ({
    ...config,
    value: Number(value),
    label: config.name,
  }))
  .sort((a, b) => a.sort - b.sort);
